import { supabase } from '../lib/supabase';
import { Operator } from '../types/database';

export class OperatorAuthService {
  /**
   * Validate operator code and return operator info
   * Uses RPC function to bypass RLS policies and prevent infinite recursion
   */
  async validateOperatorCode(code: string): Promise<Operator | null> {
    try {
      // Call the database function that bypasses RLS
      const { data, error } = await supabase.rpc('validate_operator_code', {
        input_code: code,
      });

      console.log('DEBUG: Full RPC response - data:', data, 'error:', error);

      if (error) {
        console.error('Error validating operator code:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.log('No operator found with code:', code);
        return null;
      }

      // The function returns renamed columns to avoid ambiguity
      // Map them back to the Operator interface
      const result = data[0];
      console.log('DEBUG: Raw result from validate_operator_code:', result);
      console.log('DEBUG: operator_permissions from result:', result.operator_permissions);
      console.log('DEBUG: typeof operator_permissions:', typeof result.operator_permissions);
      console.log('DEBUG: JSON.stringify(operator_permissions):', JSON.stringify(result.operator_permissions));

      const operator: Operator = {
        id: result.operator_id,
        role: result.operator_role,
        name: result.operator_name,
        department: result.operator_department,
        permissions: result.operator_permissions,
        is_active: result.operator_is_active,
        created_at: result.operator_created_at,
        last_login: result.operator_last_login,
        location_id: result.operator_location_id,
        operator_code: result.operator_code,
      };

      console.log('DEBUG: Constructed operator object:', operator);
      console.log('DEBUG: operator.permissions:', operator.permissions);

      return operator;
    } catch (err) {
      console.error('Exception validating operator code:', err);
      return null;
    }
  }

  /**
   * Update operator's last_login timestamp
   */
  async updateLastLogin(operatorId: string): Promise<void> {
    try {
      const { error } = await supabase
        .schema('operations')
        .from('operators')
        .update({ last_login: new Date().toISOString() })
        .eq('id', operatorId);

      if (error) {
        console.error('Error updating last_login:', error);
      }
    } catch (err) {
      console.error('Exception updating last_login:', err);
    }
  }

  /**
   * Get operator by ID
   */
  async getOperator(operatorId: string): Promise<Operator | null> {
    try {
      const { data, error } = await supabase
        .schema('operations')
        .from('operators')
        .select('*')
        .eq('id', operatorId)
        .single();

      if (error) {
        console.error('Error fetching operator:', error);
        return null;
      }

      return data as Operator;
    } catch (err) {
      console.error('Exception fetching operator:', err);
      return null;
    }
  }

  /**
   * Store operator in session storage
   */
  storeOperatorSession(operator: Operator): void {
    console.log('DEBUG: storeOperatorSession called with:', operator);
    console.log('DEBUG: operator.permissions before stringify:', operator.permissions);
    const stringified = JSON.stringify(operator);
    console.log('DEBUG: Stringified operator:', stringified);
    sessionStorage.setItem('current_operator', stringified);

    // Verify what was stored
    const stored = sessionStorage.getItem('current_operator');
    console.log('DEBUG: Retrieved from sessionStorage immediately after storing:', stored);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('DEBUG: Parsed back from sessionStorage:', parsed);
      console.log('DEBUG: Parsed permissions:', parsed.permissions);
    }
  }

  /**
   * Get operator from session storage
   */
  getOperatorSession(): Operator | null {
    const stored = sessionStorage.getItem('current_operator');
    if (!stored) return null;

    try {
      return JSON.parse(stored) as Operator;
    } catch (err) {
      console.error('Error parsing stored operator:', err);
      return null;
    }
  }

  /**
   * Clear operator session
   */
  clearOperatorSession(): void {
    sessionStorage.removeItem('current_operator');
  }
}

export const operatorAuthService = new OperatorAuthService();
